import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchRuleSets = createAsyncThunk(
  "filterSimulator/fetchRuleSets",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/network-rule-sets`, {
        headers: getAuthHeaders(),
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  }
);

export const fetchExtensionScripts = createAsyncThunk(
  "filterSimulator/fetchExtensionScripts",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_BASE_URL}/extension-scripts`, {
        headers: getAuthHeaders(),
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || err.message);
    }
  }
);

let nextRuleId = 1;

const initialState = {
  targetUrl: "",
  selectedScriptId: null,
  selectedRuleSetIds: [],
  filtersEnabled: true,
  simulationStatus: "idle", // idle, running, complete
  networkResults: [], // { id, url, method, type, status, blocked, matchedRule, size, duration }
  elementRules: [], // { id, selector, matchCount, enabled }
  scriptSteps: [], // { step, action, detail, status } — status: pass/fail/pending
  conflictingFilters: [], // { stepIndex, rulePattern }
  stats: { allowed: 0, blocked: 0, errors: 0, elementsHidden: 0 },
  activeTab: 0,
  // Rule editor state
  editorRules: [], // { id, pattern, type, priority }
  testPattern: "",
  // Available data from API
  availableRuleSets: [],
  availableScripts: [],
  loading: false,
  error: null,
  simulationTime: 0,
  compareMode: false,
};

const filterSimulatorSlice = createSlice({
  name: "filterSimulator",
  initialState,
  reducers: {
    setTargetUrl(state, action) {
      state.targetUrl = action.payload;
    },
    setSelectedScriptId(state, action) {
      state.selectedScriptId = action.payload;
    },
    setSelectedRuleSetIds(state, action) {
      state.selectedRuleSetIds = action.payload;
    },
    setFiltersEnabled(state, action) {
      state.filtersEnabled = action.payload;
    },
    setSimulationStatus(state, action) {
      state.simulationStatus = action.payload;
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    },
    setCompareMode(state, action) {
      state.compareMode = action.payload;
    },
    setTestPattern(state, action) {
      state.testPattern = action.payload;
    },
    setSimulationTime(state, action) {
      state.simulationTime = action.payload;
    },
    addNetworkResult(state, action) {
      state.networkResults.push(action.payload);
      if (action.payload.blocked) {
        state.stats.blocked += 1;
      } else if (action.payload.status >= 400) {
        state.stats.errors += 1;
      } else {
        state.stats.allowed += 1;
      }
    },
    clearNetworkResults(state) {
      state.networkResults = [];
      state.stats = { allowed: 0, blocked: 0, errors: 0, elementsHidden: state.stats.elementsHidden };
    },
    setElementRules(state, action) {
      state.elementRules = action.payload;
      state.stats.elementsHidden = action.payload.filter((r) => r.enabled).length;
    },
    toggleElementRule(state, action) {
      const rule = state.elementRules.find((r) => r.id === action.payload);
      if (rule) {
        rule.enabled = !rule.enabled;
        state.stats.elementsHidden = state.elementRules.filter((r) => r.enabled).length;
      }
    },
    addElementRule(state, action) {
      state.elementRules.push({
        id: nextRuleId++,
        selector: action.payload.selector || "",
        matchCount: action.payload.matchCount || 0,
        enabled: true,
      });
      state.stats.elementsHidden = state.elementRules.filter((r) => r.enabled).length;
    },
    removeElementRule(state, action) {
      state.elementRules = state.elementRules.filter((r) => r.id !== action.payload);
      state.stats.elementsHidden = state.elementRules.filter((r) => r.enabled).length;
    },
    setScriptSteps(state, action) {
      state.scriptSteps = action.payload;
    },
    updateScriptStep(state, action) {
      const { index, status } = action.payload;
      if (state.scriptSteps[index]) {
        state.scriptSteps[index].status = status;
      }
    },
    addConflictingFilter(state, action) {
      state.conflictingFilters.push(action.payload);
    },
    clearConflictingFilters(state) {
      state.conflictingFilters = [];
    },
    // Editor rules
    setEditorRules(state, action) {
      state.editorRules = action.payload;
    },
    addEditorRule(state, action) {
      state.editorRules.push({
        id: nextRuleId++,
        pattern: action.payload.pattern || "",
        type: action.payload.type || "block",
        priority: state.editorRules.length + 1,
      });
    },
    updateEditorRule(state, action) {
      const { id, ...updates } = action.payload;
      const rule = state.editorRules.find((r) => r.id === id);
      if (rule) {
        Object.assign(rule, updates);
      }
    },
    removeEditorRule(state, action) {
      state.editorRules = state.editorRules.filter((r) => r.id !== action.payload);
      state.editorRules.forEach((r, i) => {
        r.priority = i + 1;
      });
    },
    reorderEditorRules(state, action) {
      state.editorRules = action.payload;
      state.editorRules.forEach((r, i) => {
        r.priority = i + 1;
      });
    },
    resetSimulation(state) {
      state.targetUrl = "";
      state.selectedScriptId = null;
      state.selectedRuleSetIds = [];
      state.filtersEnabled = true;
      state.simulationStatus = "idle";
      state.networkResults = [];
      state.elementRules = [];
      state.scriptSteps = [];
      state.conflictingFilters = [];
      state.stats = { allowed: 0, blocked: 0, errors: 0, elementsHidden: 0 };
      state.editorRules = [];
      state.testPattern = "";
      state.simulationTime = 0;
      state.compareMode = false;
      state.activeTab = 0;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRuleSets.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRuleSets.fulfilled, (state, action) => {
        state.loading = false;
        state.availableRuleSets = action.payload;
      })
      .addCase(fetchRuleSets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch rule sets";
      })
      .addCase(fetchExtensionScripts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExtensionScripts.fulfilled, (state, action) => {
        state.loading = false;
        state.availableScripts = action.payload;
      })
      .addCase(fetchExtensionScripts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch extension scripts";
      });
  },
});

export const {
  setTargetUrl,
  setSelectedScriptId,
  setSelectedRuleSetIds,
  setFiltersEnabled,
  setSimulationStatus,
  setActiveTab,
  setCompareMode,
  setTestPattern,
  setSimulationTime,
  addNetworkResult,
  clearNetworkResults,
  setElementRules,
  toggleElementRule,
  addElementRule,
  removeElementRule,
  setScriptSteps,
  updateScriptStep,
  addConflictingFilter,
  clearConflictingFilters,
  setEditorRules,
  addEditorRule,
  updateEditorRule,
  removeEditorRule,
  reorderEditorRules,
  resetSimulation,
} = filterSimulatorSlice.actions;

export default filterSimulatorSlice.reducer;
